// Facebook & Instagram Lead Ads (D-148, INT-06), on its own page (D-156): connect with a Facebook login, choose
// the Pages (and, if wanted, only some forms), choose who gets new customers (for all forms, and for a form its own
// choice, D-154), bring in older leads, and see the latest
// leads with what became of each. Facebook sends the owner back to /integrations; main.tsx moves its answer behind the
// #, and useFacebookReturn() reads it.
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { useMember } from "../app/session";
import { Card, ConfirmButton, errorMessage, ErrorNote, Loading, useAction, useLoad } from "../app/ui";
import { fmtDateTime } from "../lib/format";
import { missingPermissions } from "../lib/integrations";
import type {
  FacebookAssign,
  FacebookForm,
  FacebookLeadRow,
  FacebookLogin,
  FacebookPage,
  FacebookPageChoice,
  FacebookState,
  Member,
} from "../lib/types";
import { PersonPicker } from "./Import";

export type FacebookReturn = { code: string; state: string } | { error: string };

/** Facebook's answer after its login, if the address carries one. */
export function useFacebookReturn(): FacebookReturn | null {
  const q = new URLSearchParams(useLocation().search);
  const code = q.get("code");
  const state = q.get("state");
  if (code && state) return { code, state };
  if (q.get("error")) {
    return {
      error: q.get("error_reason") === "user_denied" ? "The Facebook login was cancelled." : "Facebook said no.",
    };
  }
  return null;
}

export function FacebookLeadAds({ back, onChanged }: { back: FacebookReturn | null; onChanged: () => void }) {
  const { api, tz } = useMember();
  const state = useLoad(() => api.facebookState(), [api]);
  const { busy, run } = useAction();
  const navigate = useNavigate();
  const [choices, setChoices] = useState<FacebookPageChoice[] | null>(null);
  const [given, setGiven] = useState<Pick<FacebookLogin, "granted" | "declined"> | null>(null);
  const [finishing, setFinishing] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const handled = useRef<string | null>(null);
  const changed = () => {
    onChanged();
    void state.reload();
  };

  // Facebook sent the owner back: the login is finished once, then its Pages are offered.
  useEffect(() => {
    if (!back) return;
    const key = "error" in back ? back.error : back.state;
    if (handled.current === key) return;
    handled.current = key;
    navigate("/integrations/facebook-lead-ads", { replace: true });
    if ("error" in back) {
      setProblem(back.error);
      return;
    }
    setFinishing(true);
    setProblem(null);
    api.facebookFinish(back.code, back.state)
      .then((r) => {
        setChoices(r.pages);
        setGiven({ granted: r.granted, declined: r.declined });
        changed();
      })
      .catch((e) => setProblem(errorMessage(e)))
      .finally(() => setFinishing(false));
  }, [back]);

  // An import in progress is watched until it ends.
  useEffect(() => {
    if (state.data?.import?.status !== "running") return;
    const timer = setInterval(() => void state.reload(), 5000);
    return () => clearInterval(timer);
  }, [state.data?.import?.status]);

  const login = () =>
    void run(async () => {
      window.location.assign(await api.facebookStart(`${window.location.origin}/integrations`));
    });
  const addPages = () =>
    void run(async () => {
      setChoices(await api.facebookPages());
    }).then((ok) => ok || login());

  if (finishing) return <Loading text="Finishing the Facebook login…" />;
  if (state.loading && !state.data) return <Loading />;
  const s = state.data;

  // On its page, each part is its own card (D-156): the connection, the Pages and their forms, who gets new
  // customers, older leads, and the latest leads.
  return (
    <div className="stack facebook">
      <ErrorNote error={state.error} />
      {problem && <p className="danger-text">{problem}</p>}
      {choices
        ? (
          <Card>
            <ChoosePages
              choices={choices}
              given={given}
              onLogin={login}
              onCancel={() => setChoices(null)}
              onDone={() => {
                setChoices(null);
                changed();
              }}
            />
          </Card>
        )
        : !s || s.status === "disconnected"
        ? (
          <Card title="Connect">
            <p className="muted">
              Log in with the Facebook account that runs your ads, then choose the Pages whose lead forms should come
              into Voxowa.
            </p>
            <AccessNote />
            <div className="actions">
              <button type="button" className="primary" onClick={login} disabled={busy}>
                {busy ? "Opening Facebook…" : "Connect with Facebook"}
              </button>
            </div>
          </Card>
        )
        : (
          <>
            <Card
              title="Connection"
              actions={
                <>
                  {s.status !== "paused" && (
                    <button
                      type="button"
                      className="small"
                      disabled={busy}
                      onClick={() =>
                        void run(() => api.facebookSync(), "Facebook is read; any leads missed are in.").then((ok) =>
                          ok && changed()
                        )}
                    >
                      Read now
                    </button>
                  )}
                  <ConfirmButton
                    label="Disconnect"
                    confirm="Stop bringing in Facebook leads?"
                    disabled={busy}
                    onConfirm={() =>
                      void run(() => api.facebookDisconnect(), "Facebook is disconnected.").then((ok) =>
                        ok && changed()
                      )}
                  />
                </>
              }
            >
              {s.status === "error" && (
                <div className="stack">
                  <p className="danger-text">{s.last_error ?? "Facebook stopped giving Voxowa its leads."}</p>
                  <AccessNote />
                  <div className="actions">
                    <button type="button" className="primary" onClick={login} disabled={busy}>
                      Reconnect with Facebook
                    </button>
                  </div>
                </div>
              )}
              <dl className="integration-facts">
                <dt>Facebook login</dt>
                <dd>{s.facebook_user ?? "–"}</dd>
                <dt>Leads</dt>
                <dd>
                  {s.counts.today} today · {s.counts.week} this week
                  {s.counts.waiting > 0 && <span className="faint">{` · ${s.counts.waiting} waiting`}</span>}
                  {s.counts.skipped_week > 0 && <span className="faint">{` · ${s.counts.skipped_week} skipped`}</span>}
                </dd>
                <dt>Last read</dt>
                <dd>{s.last_sync_at ? fmtDateTime(s.last_sync_at, tz) : "Not yet"}</dd>
              </dl>
            </Card>
            <Card
              title="Pages and forms"
              actions={<button type="button" className="small" onClick={addPages} disabled={busy}>Add a Page</button>}
            >
              <div className="stack">
                {s.pages.length === 0 && <p className="muted">No Page yet: add one to start bringing in its leads.</p>}
                {s.pages.map((p) => <PageRow key={p.id} page={p} onChanged={changed} />)}
              </div>
            </Card>
            <Card>
              <WhoGets
                assign={s.assign}
                perForm={s.pages.some((p) => p.forms.some((f) => f.assign))}
                onChanged={changed}
              />
            </Card>
            {s.status !== "paused" && (
              <Card>
                <BringIn state={s} onChanged={changed} />
              </Card>
            )}
            <Card>
              <LatestLeads leads={s.leads} tz={tz} />
              <p className="faint">
                To test, open Meta's Lead Ads Testing Tool, choose your Page and form, and submit the form's preview
                with a real phone number that is not a team member's. It reaches the telecaller within a minute.
              </p>
            </Card>
          </>
        )}
    </div>
  );
}

/** What a Page needs before its leads can be read. */
function AccessNote() {
  return (
    <ul className="muted small-list">
      <li>You need to be able to advertise for the Page (or manage it).</li>
      <li>
        If the Page limits who may download its leads, give Voxowa access in Meta Business Suite: Settings,
        Integrations, Leads access, then the Page, then CRMs.
      </li>
    </ul>
  );
}

function ChoosePages(
  { choices, given, onLogin, onCancel, onDone }: {
    choices: FacebookPageChoice[];
    given: Pick<FacebookLogin, "granted" | "declined"> | null;
    onLogin: () => void;
    onCancel: () => void;
    onDone: () => void;
  },
) {
  const { api } = useMember();
  const { busy, run } = useAction();
  const [chosen, setChosen] = useState<string[]>(
    choices.filter((c) => c.connected || (choices.length === 1 && c.may_read_leads)).map((c) => c.id),
  );
  const [failed, setFailed] = useState<{ name: string; error: string }[]>([]);
  const fresh = chosen.filter((id) => !choices.find((c) => c.id === id)?.connected);
  const connect = () =>
    void run(async () => {
      const results = await api.facebookChoose(chosen);
      const bad = results.filter((r) => r.error).map((r) => ({ name: r.name ?? r.id, error: r.error! }));
      setFailed(bad);
      if (bad.length === results.length) throw new Error("No Page could be connected.");
    }, "Connected. New leads from these Pages now come into Voxowa.").then((ok) => ok && onDone());
  const missing = missingPermissions(given?.granted);
  const lacking = missing.length > 0 && <Missing missing={missing} declined={given?.declined ?? []} />;
  if (choices.length === 0) {
    return (
      <div className="stack">
        <p className="danger-text">Facebook gave Voxowa no Page with this login.</p>
        {lacking || (
          <ul className="muted small-list">
            <li>When Facebook asks which Pages Voxowa may use, choose the Page whose leads you want.</li>
            <li>
              If the Page belongs to a business portfolio, you need full control of it there, or the right to advertise
              for it (Meta Business Suite: Settings, Pages, the Page, People).
            </li>
          </ul>
        )}
        <div className="actions">
          <button type="button" onClick={onCancel}>Close</button>
          <button type="button" className="primary" onClick={onLogin}>Log in again</button>
        </div>
      </div>
    );
  }
  return (
    <div className="stack">
      {lacking}
      <h4>Choose the Pages to bring leads from</h4>
      {choices.map((c) => (
        <label className="row" key={c.id}>
          <input
            type="checkbox"
            checked={chosen.includes(c.id)}
            disabled={!c.may_read_leads && !c.connected}
            onChange={(e) => setChosen(e.target.checked ? [...chosen, c.id] : chosen.filter((x) => x !== c.id))}
          />
          {c.name}
          {c.connected && <span className="badge ok">Connected</span>}
          {!c.may_read_leads && <span className="faint">You cannot advertise for this Page</span>}
        </label>
      ))}
      {failed.map((f) => <p className="danger-text" key={f.name}>{f.name}: {f.error}</p>)}
      <div className="actions">
        <button type="button" onClick={onCancel} disabled={busy}>Cancel</button>
        <button type="button" className="primary" onClick={connect} disabled={busy || chosen.length === 0}>
          {busy
            ? "Connecting…"
            : fresh.length
            ? `Connect ${fresh.length === 1 ? "this Page" : `${fresh.length} Pages`}`
            : chosen.length
            ? "Connect again"
            : "Connect"}
        </button>
      </div>
    </div>
  );
}

/** The permissions a login lacks, each with what it is for, and how to give them. */
function Missing({ missing, declined }: { missing: [string, string][]; declined: string[] }) {
  return (
    <div className="stack">
      <p className="danger-text">Facebook did not give Voxowa everything it needs:</p>
      <ul className="small-list">
        {missing.map(([p, why]) => (
          <li key={p}>
            <code>{p}</code> <span className="muted">{why}</span>
            {declined.includes(p) && <span className="faint">(turned down)</span>}
          </li>
        ))}
      </ul>
      <p className="muted">
        Log in again and allow everything Facebook asks. If Facebook does not ask again, remove Voxowa first: Facebook,
        Settings and privacy, Settings, Business integrations. If Facebook never asks for these, they are missing from
        the app's Facebook Login for Business configuration (see docs/facebook-lead-ads.md).
      </p>
    </div>
  );
}

/** One connected Page: its state, its forms (all, or the ones chosen) with who gets each one's leads, and Remove. */
function PageRow({ page, onChanged }: { page: FacebookPage; onChanged: () => void }) {
  const { api, tz } = useMember();
  const { busy, run } = useAction();
  const members = useLoad(() => api.members(), [api]);
  const team = (members.data ?? []).filter((m) => m.status === "active");
  // The forms it brings, but not archived ones (no new leads come from them) unless they still have their own choice.
  const brought = (page.form_ids === null ? page.forms : page.forms.filter((f) => page.form_ids!.includes(f.id)))
    .filter((f) => !f.status || f.status === "ACTIVE" || f.assign);
  const [editing, setEditing] = useState(false);
  const [all, setAll] = useState(page.form_ids === null);
  const [forms, setForms] = useState<string[]>(page.form_ids ?? page.forms.map((f) => f.id));
  const save = () =>
    void run(() => api.facebookSetForms(page.id, all ? null : forms), "Saved.").then((ok) => {
      if (ok) {
        setEditing(false);
        onChanged();
      }
    });
  const formCount = page.form_ids === null
    ? "every form"
    : `${page.form_ids.length} of ${page.forms.length} ${page.forms.length === 1 ? "form" : "forms"}`;
  return (
    <div className="facebook-page">
      <div className="row between">
        <strong>{page.name ?? page.id}</strong>
        {page.status === "error" ? <span className="badge danger">Needs attention</span> : (
          <span className="faint">
            {page.last_lead_at ? `Last lead ${fmtDateTime(page.last_lead_at, tz)}` : "No lead yet"}
          </span>
        )}
      </div>
      {page.last_error && <p className="danger-text">{page.last_error}</p>}
      <div className="row between">
        <span className="muted">Brings leads from {formCount}</span>
        <span className="row">
          <button type="button" className="link" onClick={() => setEditing(!editing)}>
            {editing ? "Close" : "Choose forms"}
          </button>
          <ConfirmButton
            label="Remove"
            confirm="Stop this Page's leads?"
            disabled={busy}
            onConfirm={() =>
              void run(() => api.facebookRemovePage(page.id), "The Page is removed.").then((ok) => ok && onChanged())}
          />
        </span>
      </div>
      {editing && (
        <div className="stack">
          <label className="row">
            <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} />
            Every form, also ones made later
          </label>
          {!all && page.forms.map((f) => (
            <label className="row" key={f.id}>
              <input
                type="checkbox"
                checked={forms.includes(f.id)}
                onChange={(e) => setForms(e.target.checked ? [...forms, f.id] : forms.filter((x) => x !== f.id))}
              />
              {f.name ?? f.id}
              {f.status && f.status !== "ACTIVE" && <span className="faint">{f.status.toLowerCase()}</span>}
            </label>
          ))}
          <div className="actions">
            <button type="button" className="primary" onClick={save} disabled={busy || (!all && forms.length === 0)}>
              Save
            </button>
          </div>
        </div>
      )}
      {brought.length > 0 && (
        <div className="facebook-forms">
          <span className="muted">Who gets each form's new customers</span>
          {brought.map((f) => <FormOwner key={f.id} form={f} team={team} onChanged={onChanged} />)}
        </div>
      )}
    </div>
  );
}

type FormMode = FacebookAssign["mode"] | "default";

/** Who gets one form's new customers (D-154): as for all forms, one person, several in turn, or nobody yet. */
function FormOwner({ form, team, onChanged }: { form: FacebookForm; team: Member[]; onChanged: () => void }) {
  const { api } = useMember();
  const { busy, run } = useAction();
  const [editing, setEditing] = useState(false);
  const [mode, setMode] = useState<FormMode>(form.assign?.mode ?? "default");
  const [chosen, setChosen] = useState<string[]>(form.assign?.members ?? []);
  const names = new Map(team.map((m) => [m.id, m.display_name]));
  const people = (a: FacebookAssign) =>
    a.mode === "none"
      ? "nobody yet"
      : a.mode === "one"
      ? names.get(a.members[0]) ?? "–"
      : `in turn: ${a.members.map((id) => names.get(id) ?? "–").join(", ")}`;
  const now = form.assign ? people(form.assign) : "as for all forms";
  const withPeople = mode === "one" || mode === "split";
  const save = () =>
    void run(() => api.facebookSetFormAssignment(form.id, mode, withPeople ? chosen : []), "Saved.").then((ok) => {
      if (ok) {
        setEditing(false);
        onChanged();
      }
    });
  return (
    <div className="facebook-form">
      <div className="row between">
        <span>
          {form.name ?? form.id}
          {form.status && form.status !== "ACTIVE" && <span className="faint">{` ${form.status.toLowerCase()}`}</span>}
          {form.new && !form.assign && <span className="badge brand">New</span>}
        </span>
        <span className="row">
          <span className={form.assign ? "" : "faint"}>{now}</span>
          <button type="button" className="link" onClick={() => setEditing(!editing)}>
            {editing ? "Close" : "Change"}
          </button>
        </span>
      </div>
      {editing && (
        <div className="stack">
          {([["default", "As for all forms"], ["one", "One person"], ["split", "Several people, in turn"], [
            "none",
            "Nobody yet",
          ]] as [FormMode, string][]).map(([value, label]) => (
            <label className="row" key={value}>
              <input
                type="radio"
                name={`facebook-form-${form.id}`}
                checked={mode === value}
                onChange={() => {
                  setMode(value);
                  if (value === "one") setChosen((c) => c.slice(0, 1));
                }}
              />
              {label}
            </label>
          ))}
          {withPeople && (
            <PersonPicker
              team={team}
              chosen={chosen}
              onChange={setChosen}
              single={mode === "one"}
              name={`facebook-form-${form.id}-person`}
            />
          )}
          <div className="actions">
            <button
              type="button"
              className="primary"
              onClick={save}
              disabled={busy || (withPeople && chosen.length === 0)}
            >
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Who gets new customers: nobody, one person, or several in turn. Facebook's, or another integration's (D-158). */
export function WhoGets(
  { assign, perForm = false, onChanged, save, hint }: {
    assign: FacebookAssign;
    perForm?: boolean;
    onChanged: () => void;
    /** Where the choice goes; Facebook's when not given. */
    save?: (mode: FacebookAssign["mode"], members: string[]) => Promise<void>;
    /** What the choice covers, under the heading; Facebook's forms when not given. */
    hint?: string;
  },
) {
  const { api } = useMember();
  const { busy, run } = useAction();
  const members = useLoad(() => api.members(), [api]);
  const [mode, setMode] = useState(assign.mode);
  const [chosen, setChosen] = useState<string[]>(assign.members);
  const team = (members.data ?? []).filter((m) => m.status === "active");
  const changed = mode !== assign.mode || chosen.join() !== assign.members.join();
  const persist = () =>
    void run(
      () => (save ?? ((m, ids) => api.facebookSetAssignment(m, ids)))(mode, mode === "none" ? [] : chosen),
      "Saved.",
    ).then((ok) => ok && onChanged());
  return (
    <div className="field">
      {/* With forms given out one by one, this is where a new form's leads go until someone chooses for it (D-155). */}
      <h4>{perForm ? "New forms, and forms without their own choice, go to" : "New customers go to"}</h4>
      <span className="hint">
        {hint ?? (perForm
          ? "A form made later is marked New under its Page; its leads come here until you choose for it."
          : "From every form, also ones made later. To give a form its own telecaller, use Change under its Page.")}
      </span>
      {([["split", "Several people, in turn"], ["one", "One person"], ["none", "Nobody yet"]] as [
        FacebookAssign["mode"],
        string,
      ][]).map(([value, label]) => (
        <label className="row" key={value}>
          <input
            type="radio"
            name="facebook-assign"
            checked={mode === value}
            onChange={() => {
              setMode(value);
              if (value === "one") setChosen((c) => c.slice(0, 1));
            }}
          />
          {label}
        </label>
      ))}
      {mode !== "none" && <PersonPicker team={team} chosen={chosen} onChange={setChosen} single={mode === "one"} />}
      <span className="hint">
        {mode === "none"
          ? "Leads nobody owns wait in Leads until you give them to someone."
          : "A new customer is on their Today list, on top, within a minute. A customer Voxowa knows keeps their owner."}
      </span>
      {changed && (
        <div className="actions">
          <button
            type="button"
            className="primary"
            onClick={persist}
            disabled={busy || (mode !== "none" && chosen.length === 0)}
          >
            Save
          </button>
        </div>
      )}
    </div>
  );
}

/** Older leads, on request: the last 7, 30 or 90 days (Meta keeps 90). */
function BringIn({ state, onChanged }: { state: FacebookState; onChanged: () => void }) {
  const { api, tz } = useMember();
  const { busy, run } = useAction();
  const [days, setDays] = useState<7 | 30 | 90>(90);
  const [assign, setAssign] = useState(false);
  const job = state.import;
  if (state.pages.length === 0) return null;
  return (
    <div className="field">
      <h4>Leads from before you connected</h4>
      {job?.status === "running" && (
        <p className="muted">
          Bringing in the last {job.days} days: {job.leads ?? 0} read, {job.added ?? 0} new so far…
        </p>
      )}
      {job?.status === "done" && (
        <p className="muted">
          The last {job.days} days are in{job.finished_at ? ` (${fmtDateTime(job.finished_at, tz)})` : ""}:{" "}
          {job.leads ?? 0} leads read, {job.added ?? 0} new enquiries
          {job.skipped ? `, ${job.skipped} skipped` : ""}.
        </p>
      )}
      {job?.status === "failed" && <p className="danger-text">The import stopped: {job.error ?? "it failed"}.</p>}
      {job?.status !== "running" && (
        <>
          {state.calls_since && Date.now() - days * 86_400_000 < new Date(state.calls_since).getTime() && (
            <p className="muted">
              Voxowa has your team's calls since{" "}
              {fmtDateTime(state.calls_since, tz)}. A lead from before then may have been called on a phone already; it
              comes in as a new customer unless Voxowa knows the number.
            </p>
          )}
          <div className="row">
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value) as 7 | 30 | 90)}
              aria-label="How far back"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            <button
              type="button"
              onClick={() =>
                void run(() => api.facebookImport(days, assign), "Bringing them in.").then((ok) => ok && onChanged())}
              disabled={busy}
            >
              Bring them in
            </button>
          </div>
          <label className="row">
            <input type="checkbox" checked={assign} onChange={(e) => setAssign(e.target.checked)} />
            <span>Give new customers out as above (otherwise they wait, with nobody, for you to give out)</span>
          </label>
        </>
      )}
    </div>
  );
}

/** How a lead came in: Meta's notification the moment it was filled in, the six-hourly read, or an import. */
const VIA: Record<FacebookLeadRow["via"], string> = { webhook: "live", catch_up: "read later", import: "imported" };

const STATUS: Record<FacebookLeadRow["status"], string> = {
  done: "In",
  pending: "Waiting",
  skipped: "Skipped",
  failed: "Failed",
};

function LatestLeads({ leads, tz }: { leads: FacebookLeadRow[]; tz: string }) {
  if (leads.length === 0) return <p className="muted">No Facebook lead yet.</p>;
  return (
    <div className="field">
      <h4>Latest leads</h4>
      <div className="table-wrap">
        <table>
          <tbody>
            {leads.map((l) => (
              <tr key={l.id}>
                <td className="faint">{fmtDateTime(l.created_time ?? l.received_at, tz)}</td>
                <td>
                  {l.lead_id ? <Link to={`/leads/${l.lead_id}`}>{l.name ?? l.phone ?? "Customer"}</Link> : "–"}
                  {l.form && <div className="faint">{l.form}</div>}
                </td>
                <td>
                  <span className={l.status === "done" ? "" : l.status === "pending" ? "faint" : "danger-text"}>
                    {STATUS[l.status]}
                  </span>
                  <div className="faint">
                    {l.status === "done" ? (l.owner ? `To ${l.owner}` : "Nobody yet") : l.reason ?? ""}
                    {` · ${VIA[l.via]}`}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
